import React, { useEffect, useState } from "react";
import moment from "moment";
import { toast } from "react-toastify";

import Modal from "../../../components/Modal";
import Button from "../../../components/Button";
import InputSelect from "../../../components/InputSelect";
import InputTextarea from "../../../components/InputTextarea";
import SelectCompany from "../../../components/selects/SelectCompany";

import api from "../../../services/api";
import { renderError } from "../../../helpers/errors";
import { convertIntToMoney } from "../../../helpers/conversions";

import { getDfeStatusLabel, getEmitterLabel, getXmlNotice } from "../helpers.dfe";

const MANIFESTATIONS = [
    { label: 'Confirmação da operação', value: 'CONFIRMACAODAOPERACAO' },
    { label: 'Desconhecimento da operação', value: 'DESCONHECIMENTODAOPERACAO' },
    { label: 'Operação não realizada', value: 'OPERACAONAOREALIZADA' },
];

/**
 * Triagem de uma nota recebida da SEFAZ (BDM-1038).
 *
 * As duas decisões que a tela tira do automático: o que é compra (só então
 * vira nota de entrada) e a manifestação conclusiva — que é declaração fiscal
 * em nome da empresa e por isso nunca sai daqui sozinha.
 */
export const ModalDfeInvoice = ({ show, invoice, canManage, close, onChanged }) => {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [detail, setDetail] = useState(null);
    const [items, setItems] = useState([]);
    const [suggestions, setSuggestions] = useState([]);

    const [companyId, setCompanyId] = useState(null);
    const [purchaseOrderId, setPurchaseOrderId] = useState(null);

    const [manifesting, setManifesting] = useState(false);
    const [operation, setOperation] = useState(MANIFESTATIONS[0].value);
    const [justification, setJustification] = useState('');

    useEffect(() => {
        if (!show || !invoice) return;

        setDetail(invoice);
        setItems([]);
        setSuggestions([]);
        setCompanyId(invoice.company_id ?? null);
        setPurchaseOrderId(null);
        setManifesting(false);
        setOperation(MANIFESTATIONS[0].value);
        setJustification('');

        setLoading(true);

        api.get(`/dfe-invoice/${invoice.id}`).then(res => {
            setDetail(res.data.resource);
            setItems(res.data.items ?? []);
            setSuggestions(res.data.purchase_orders ?? []);
        }).catch(renderError).then(() => setLoading(false));
    }, [show, invoice]);

    const current = detail ?? invoice;
    const isPending = current?.status === 'pending';

    const importInvoice = () => {
        setSaving(true);

        api.post(`/dfe-invoice/${current.id}/import`, {
            company_id: companyId,
            purchase_orders: purchaseOrderId ? [{ id: purchaseOrderId }] : [],
        }).then(() => {
            toast.success('Nota importada para as notas de entrada');
            onChanged();
        }).catch(renderError).then(() => setSaving(false));
    };

    const updateStatus = (status, message) => {
        setSaving(true);

        api.patch(`/dfe-invoice/${current.id}/status`, { status }).then(() => {
            toast.success(message);
            onChanged();
        }).catch(renderError).then(() => setSaving(false));
    };

    const manifest = () => {
        setSaving(true);

        api.post(`/dfe-invoice/${current.id}/manifest`, {
            operation,
            justification: justification || null,
        }).then(() => {
            toast.success('Manifestação enviada. A SEFAZ libera os itens em alguns minutos.');
            onChanged();
        }).catch(renderError).then(() => setSaving(false));
    };

    const purchaseOrderOptions = [
        { label: 'Sem compra vinculada', value: null },
        ...suggestions.map(option => ({
            label: `#${option.code} — ${convertIntToMoney(option.total, true)}`,
            value: option.id,
        })),
    ];

    const notice = getXmlNotice(current);

    if (!current) return null;

    return (
        <Modal
            className={`dfe-invoice-modal`}
            size={`big`}
            title={`Nota ${current.number ?? ''} de ${getEmitterLabel(current)}`}
            show={show}
            close={close}
            footer={
                <>
                    <Button type={`secondary`} size={`small`} text={`Fechar`} action={close} />
                    {canManage && isPending && !manifesting &&
                        <>
                            <Button
                                type={`secondary`}
                                size={`small`}
                                text={`Ignorar`}
                                action={() => updateStatus('ignored', 'Nota ignorada')}
                                loading={saving}
                            />
                            <Button
                                type={`secondary`}
                                size={`small`}
                                text={`Já lancei essa`}
                                action={() => updateStatus('expense', 'Nota marcada como já lançada')}
                                loading={saving}
                            />
                            <Button
                                type={`primary`}
                                size={`small`}
                                text={`Importar`}
                                action={importInvoice}
                                loading={saving}
                            />
                        </>
                    }
                    {canManage && manifesting &&
                        <Button
                            type={`primary`}
                            size={`small`}
                            text={`Enviar manifestação`}
                            action={manifest}
                            loading={saving}
                        />
                    }
                </>
            }
        >
            <div className="section dfe-invoice-detail">
                <div className="dfe-summary">
                    <div>
                        <small>Emitente</small>
                        <strong>{current.emitter_name ?? getEmitterLabel(current)}</strong>
                        <span>{current.emitter_cnpj}</span>
                    </div>
                    <div>
                        <small>Emissão</small>
                        <strong>{current.issued_at ? moment(current.issued_at).format('DD/MM/YYYY') : '—'}</strong>
                    </div>
                    <div>
                        <small>Valor</small>
                        <strong>{convertIntToMoney(current.amount, true)}</strong>
                    </div>
                    <div>
                        <small>Situação</small>
                        <strong>{getDfeStatusLabel(current)}</strong>
                    </div>
                </div>

                <p className={`dfe-access-key`}>{current.access_key}</p>

                {notice && <div className={`dfe-warning`}>{notice}</div>}

                {current.manifestation_message &&
                    <div className={`dfe-warning`}>{current.manifestation_message}</div>
                }

                {!manifesting && canManage && current.can_manifest_for_full_xml &&
                    <Button
                        type={`secondary`}
                        size={`small`}
                        text={`Manifestar para ver os itens`}
                        action={() => setManifesting(true)}
                    />
                }

                {manifesting &&
                    <div className={`dfe-manifest`}>
                        <div className={`dfe-warning strong`}>
                            A confirmação da operação declara à Receita que a empresa recebeu esta
                            mercadoria — e o emitente deixa de poder cancelar a nota. A SEFAZ aceita no
                            máximo duas manifestações por nota.
                        </div>
                        <InputSelect
                            menuInBody
                            label={`Manifestação`}
                            options={MANIFESTATIONS}
                            value={operation}
                            change={setOperation}
                        />
                        {operation === 'OPERACAONAOREALIZADA' &&
                            <InputTextarea
                                label={`Justificativa (mínimo 15 caracteres)`}
                                value={justification}
                                change={setJustification}
                                rows={3}
                            />
                        }
                        <Button
                            type={`secondary`}
                            size={`small`}
                            text={`Cancelar`}
                            action={() => setManifesting(false)}
                        />
                    </div>
                }

                {!manifesting && isPending && canManage &&
                    <div className={`dfe-import`}>
                        <h4>Importar como nota de entrada</h4>
                        {!current.company_id &&
                            <SelectCompany
                                label={`Fornecedor (não encontrei este CNPJ no cadastro)`}
                                nullable={true}
                                selectedCompany={companyId}
                                setSelectedCompany={setCompanyId}
                            />
                        }
                        <InputSelect
                            menuInBody
                            label={`Pedido de compra`}
                            options={purchaseOrderOptions}
                            value={purchaseOrderId}
                            change={setPurchaseOrderId}
                        />
                    </div>
                }

                {items.length > 0 &&
                    <div className={`dfe-items`}>
                        <h4>Itens da nota</h4>
                        <ul>
                            {items.map((item, index) => (
                                <li key={index}>
                                    <span>{item.descricao ?? item.codigo ?? '—'}</span>
                                    <small>
                                        {item.quantidade ?? '—'} × {convertIntToMoney(Math.round((item.valorUnitario ?? 0) * 100), true)}
                                    </small>
                                </li>
                            ))}
                        </ul>
                    </div>
                }

                {loading && <p className={`dfe-filter-hint`}>Carregando os dados da nota...</p>}
            </div>
        </Modal>
    );
};

export default ModalDfeInvoice;
